// src/components/Establecimientos.jsx

import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit, Ban, Search, MapPin, RefreshCw, Factory, Warehouse, Ship, ParkingCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const Establecimientos = () => {
  // Estados
  const [establecimientos, setEstablecimientos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEstablecimiento, setEditingEstablecimiento] = useState(null);
  
  // Filtros y paginación
  const [sortConfig, setSortConfig] = useState({ key: 'nombre', direction: 'asc' });
  const [filters, setFilters] = useState({
    buscar: '',
    empresa_id: '',
    tipo: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 15
  });

  // Formulario
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    empresa_id: '',
    tipo: 'PLANTA',
    activo: true
  });

  const [errors, setErrors] = useState({});

  // Tipos de establecimiento
  const TIPOS = ['PLANTA', 'ALMACEN', 'PUERTO', 'COCHERA'];

  // Headers de autenticación
  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  });

  // Cargar empresas solo al montar
  useEffect(() => {
    loadEmpresas();
  }, []);

  // Cargar establecimientos cuando cambien filtros o paginación
  useEffect(() => {
    loadEstablecimientos();
  }, [pagination.current_page, filters.buscar, filters.empresa_id, filters.tipo, sortConfig]);

  // Cargar establecimientos
  const loadEstablecimientos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.current_page,
        perPage: pagination.per_page,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction
      });

      if (filters.buscar) params.append('buscar', filters.buscar);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.tipo) params.append('tipo', filters.tipo);

      const response = await fetch(`${API}/establecimientos?${params}`, {
        headers: getHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setEstablecimientos(data.data);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          last_page: data.pagination.last_page,
          from: data.pagination.from,
          to: data.pagination.to
        }));
      }
    } catch (error) {
      console.error('Error al cargar establecimientos:', error);
      Swal.fire('Error', 'No se pudieron cargar los establecimientos', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Cargar empresas
  const loadEmpresas = async () => {
    try {
      const response = await fetch(`${API}/establecimientos/empresas`, {
        headers: getHeaders()
      });
      const data = await response.json();
      setEmpresas(data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  // Abrir modal
  const openModal = () => {
    setEditingEstablecimiento(null);
    setFormData({
      nombre: '',
      direccion: '',
      empresa_id: '',
      tipo: 'PLANTA',
      activo: true
    });
    setErrors({});
    setShowModal(true);
  };

  // Cerrar modal
  const closeModal = () => {
    setShowModal(false);
    setEditingEstablecimiento(null);
    setFormData({
      nombre: '',
      direccion: '',
      empresa_id: '',
      tipo: 'PLANTA',
      activo: true
    });
    setErrors({});
  };

  // Manejar cambios en formulario
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Manejar cambio de búsqueda
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, buscar: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Manejar cambio de filtro empresa
  const handleEmpresaFilterChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, empresa_id: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Manejar cambio de filtro tipo
  const handleTipoFilterChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, tipo: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    try {
      const url = editingEstablecimiento
        ? `${API}/establecimientos/${editingEstablecimiento.id}`
        : `${API}/establecimientos`;
      
      const method = editingEstablecimiento ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: editingEstablecimiento ? 'Actualizado' : 'Creado',
          text: data.message,
          timer: 2000,
          showConfirmButton: false
        });
        closeModal();
        loadEstablecimientos();
      } else {
        if (data.errors) {
          setErrors(data.errors);
        }
        Swal.fire('Error', data.message || 'Error al guardar', 'error');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      Swal.fire('Error', 'No se pudo guardar el establecimiento', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Editar
  const handleEdit = (establecimiento) => {
    setEditingEstablecimiento(establecimiento);
    setFormData({
      nombre: establecimiento.nombre,
      direccion: establecimiento.direccion,
      empresa_id: establecimiento.empresa_id,
      tipo: establecimiento.tipo,
      activo: establecimiento.activo
    });
    setErrors({});
    setShowModal(true);
  };

  // Desactivar
  const handleDelete = async (establecimiento) => {
    const result = await Swal.fire({
      title: '⚠️ Desactivar Establecimiento',
      html: `¿Desea desactivar: <strong>${establecimiento.nombre}</strong>?<br><small class="text-gray-500">Podrá reactivarlo más tarde</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f97316',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API}/establecimientos/${establecimiento.id}`, {
          method: 'DELETE',
          headers: getHeaders()
        });

        const data = await response.json();

        if (data.success) {
          Swal.fire({
            icon: 'success',
            title: 'Desactivado',
            text: data.message,
            timer: 2000,
            showConfirmButton: false
          });
          loadEstablecimientos();
        }
      } catch (error) {
        Swal.fire('Error', 'No se pudo desactivar el establecimiento', 'error');
      }
    }
  };

  // Toggle activo
  const toggleActivo = async (establecimiento) => {
    try {
      const response = await fetch(`${API}/establecimientos/${establecimiento.id}/toggle-estado`, {
        method: 'PATCH',
        headers: getHeaders()
      });

      const data = await response.json();

      if (data.success) {
        loadEstablecimientos();
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: data.message,
          showConfirmButton: false,
          timer: 2000
        });
      }
    } catch (error) {
      Swal.fire('Error', 'No se pudo cambiar el estado', 'error');
    }
  };

  // Ordenar
  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  // Limpiar filtros
  const clearFilters = () => {
    setFilters({
      buscar: '',
      empresa_id: '',
      tipo: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  // Obtener icono según tipo
  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'PLANTA': return <Factory className="h-3.5 w-3.5" />;
      case 'ALMACEN': return <Warehouse className="h-3.5 w-3.5" />;
      case 'PUERTO': return <Ship className="h-3.5 w-3.5" />;
      case 'COCHERA': return <ParkingCircle className="h-3.5 w-3.5" />;
      default: return <Building2 className="h-3.5 w-3.5" />;
    }
  };

  // Obtener color de badge según tipo
  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'PLANTA': return 'bg-blue-100 text-blue-700';
      case 'ALMACEN': return 'bg-orange-100 text-orange-700';
      case 'PUERTO': return 'bg-cyan-100 text-cyan-700';
      case 'COCHERA': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  return (
    <div className="space-y-2">
      {/* Header */}
      <Card>
        <CardHeader className="pb-2 pt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              <CardTitle className="text-sm">Gestión de Establecimientos</CardTitle>
              <Badge variant="outline" className="text-xs">
                {pagination.total} registros
              </Badge>
            </div>
            <div className="flex gap-1.5">
              <Button 
                variant="outline" 
                size="sm"
                onClick={loadEstablecimientos}
                disabled={loading}
                className="h-7 text-xs"
              >
                <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                size="sm"
                onClick={openModal}
                className="bg-blue-600 hover:bg-blue-700 h-7 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Nuevo Establecimiento
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o dirección..."
                  value={filters.buscar}
                  onChange={handleSearchChange}
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <select
              value={filters.empresa_id}
              onChange={handleEmpresaFilterChange}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Todas las empresas</option>
              {empresas.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.nombre}</option>
              ))}
            </select>

            <select
              value={filters.tipo}
              onChange={handleTipoFilterChange}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Todos los tipos</option>
              {TIPOS.map(tipo => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>

            <Button onClick={clearFilters} variant="outline" size="sm" className="h-7 text-xs">
              Limpiar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : establecimientos.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">No hay establecimientos registrados</p>
              <Button onClick={openModal} size="sm" className="mt-3 text-xs">
                Crear primer establecimiento
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th 
                        onClick={() => handleSort('nombre')}
                        className="px-3 py-2 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-1">
                          Nombre
                          {sortConfig.key === 'nombre' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </div>
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Dirección</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Empresa</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Tipo</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">Estado</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {establecimientos.map((est) => (
                      <tr key={est.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            {getTipoIcon(est.tipo)}
                            <span className="font-medium">{est.nombre}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1 text-gray-600">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate max-w-xs">{est.direccion}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {est.empresa?.nombre || 'N/A'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={`${getTipoColor(est.tipo)} text-[10px] px-1.5 py-0.5`}>
                            {getTipoIcon(est.tipo)} {est.tipo}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => toggleActivo(est)}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                              est.activo
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            {est.activo ? 'Activo' : 'Inactivo'}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleEdit(est)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Editar"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(est)}
                              className="p-1 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                              title="Desactivar"
                            >
                              <Ban className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              <div className="flex items-center justify-between px-3 py-2 border-t bg-gray-50">
                <div className="text-xs text-gray-600">
                  Mostrando {pagination.from} a {pagination.to} de {pagination.total} registros
                </div>
                <div className="flex gap-1">
                  <Button
                    onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                    disabled={pagination.current_page === 1}
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                  >
                    Anterior
                  </Button>
                  <span className="px-2 py-1 text-xs">
                    {pagination.current_page} / {pagination.last_page}
                  </span>
                  <Button
                    onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                    disabled={pagination.current_page === pagination.last_page}
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                {editingEstablecimiento ? (
                  <><Edit className="h-4 w-4" /> Editar Establecimiento</>
                ) : (
                  <><Plus className="h-4 w-4" /> Nuevo Establecimiento</>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Ej: Planta Principal Lima"
                    required
                  />
                  {errors.nombre && <p className="text-red-500 text-[10px] mt-0.5">{errors.nombre}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Dirección <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Ej: Av. Industrial 123, Lima"
                    required
                  />
                  {errors.direccion && <p className="text-red-500 text-[10px] mt-0.5">{errors.direccion}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Empresa <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="empresa_id"
                      value={formData.empresa_id}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    >
                      <option value="">Seleccione...</option>
                      {empresas.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                      ))}
                    </select>
                    {errors.empresa_id && <p className="text-red-500 text-[10px] mt-0.5">{errors.empresa_id}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Tipo <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="tipo"
                      value={formData.tipo}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    >
                      {TIPOS.map(tipo => (
                        <option key={tipo} value={tipo}>{tipo}</option>
                      ))}
                    </select>
                    {errors.tipo && <p className="text-red-500 text-[10px] mt-0.5">{errors.tipo}</p>}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="activo"
                    checked={formData.activo}
                    onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
                  />
                  <label htmlFor="activo" className="text-xs font-medium text-gray-700 cursor-pointer">
                    Establecimiento activo
                  </label>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    onClick={closeModal}
                    variant="outline"
                    className="flex-1 h-7 text-xs"
                    disabled={saving}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-7 text-xs"
                    disabled={saving}
                  >
                    {saving ? 'Guardando...' : (editingEstablecimiento ? 'Actualizar' : 'Guardar')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Establecimientos;